import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingComponent from '../../../components/LoadingComponent';
import { fetchFlows, fetchFlowById, deleteFlow, createFlow, fetchFlowMeta, FlowResponse } from '../../../api/whatsapp.service';
import { 
  IoMdAdd, 
  IoMdTrash, 
  IoMdCreate, 
  IoMdEye, 
  IoMdClose, 
  IoMdCheckmark,
  IoMdCopy,
  IoMdDownload,
  IoMdSettings,
  IoMdArrowForward,
  IoMdArrowBack,
  IoMdCode
} from 'react-icons/io';
import { MdDragIndicator } from 'react-icons/md';

// Flow Template Types
interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  screens: FlowScreen[];
  previewUrl?: string; // Preview URL from API
}

interface FlowScreen {
  id: string;
  name: string;
  screenId: string;
  fields: FlowField[];
  navigation?: NavigationRule[];
  coverImage?: string; // Base64 encoded image
  coverImageHeight?: number;
}

interface ListItemTemplate {
  type: 'Card';
  children: Array<{
    type: 'Image' | 'Text' | 'Stepper';
    name?: string;
    src?: string; // For Image with dynamic src like "${item.image}"
    text?: string; // For Text with dynamic text like "${item.title}"
    min?: number; // For Stepper
    max?: number; // For Stepper
    value?: string; // For Stepper with dynamic value like "${item.qty}"
  }>;
}

interface FlowField {
  id: string;
  key: string;
  label: string;
  type: 'text' | 'dropdown' | 'date' | 'time' | 'number' | 'boolean' | 'list' | 'checkboxgroup' | 'heading' | 'subheading' | 'body' | 'optin' | 'textarea' | 'datepicker';
  options?: { id: string; title: string }[];
  listItems?: string[]; // Simple list items (for basic list)
  listDataSource?: string; // Data source expression like "${items}" (for advanced list)
  itemTemplate?: ListItemTemplate; // Template for rendering list items (for advanced list)
  required?: boolean;
  enabled?: boolean;
  dependsOn?: string; // Field that must be filled before this is enabled
  value?: any;
  text?: string; // For heading, subheading, body types
  navigateTo?: string; // For optin type - screen to navigate to when clicked
}

interface NavigationRule {
  fromScreen: string;
  toScreen: string;
  condition?: {
    field: string;
    operator: 'equals' | 'not_equals' | 'exists' | 'not_exists';
    value?: any;
  };
}

export default function WhatsAppFlowBuilder() {
  const navigate = useNavigate();
  
  console.log('WhatsAppFlowBuilder component rendering');
  console.log('WhatsAppFlowBuilder: Component mounted, checking sessionStorage...');
  console.log('WhatsAppFlowBuilder: wabaId in sessionStorage:', sessionStorage.getItem('wabaId'));
  
  const [loading, setLoading] = useState(false);
  const [flows, setFlows] = useState<FlowTemplate[]>([]);
  const [flowsLoading, setFlowsLoading] = useState(true);
  const [flowsError, setFlowsError] = useState<string | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<FlowTemplate | null>(null);
  
  // Get wabaId from session storage
  const [wabaId, setWabaId] = useState<string | null>(null);
  const [showTemplateSelection, setShowTemplateSelection] = useState(true);
  const [showFlowEditor, setShowFlowEditor] = useState(false);
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [showFlowPreview, setShowFlowPreview] = useState(false);
  const [previewScreenIndex, setPreviewScreenIndex] = useState(0);
  const [showImportJson, setShowImportJson] = useState(false);
  const [publishFlowName, setPublishFlowName] = useState('');
  const [publishCategory, setPublishCategory] = useState('OTHER');
  const [publishFlowJson, setPublishFlowJson] = useState('');
  const [publishFlow, setPublishFlow] = useState(true);
  
  // Current flow being edited
  const [currentFlow, setCurrentFlow] = useState<FlowTemplate | null>(null);
  const [selectedScreen, setSelectedScreen] = useState<FlowScreen | null>(null);
  const [selectedField, setSelectedField] = useState<FlowField | null>(null);
  
  // Form states
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  
  // Cover image states
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);

  // Start from template - fetch full flow details and show preview
  const handleStartFromTemplate = async (template: FlowTemplate) => {
    console.log('WhatsAppFlowBuilder: handleStartFromTemplate called with template:', template);
    
    try {
      setLoading(true);
      
      // Get wabaId from sessionStorage if not in state
      const actualWabaId = wabaId || sessionStorage.getItem('wabaId');
      
      // Always fetch full flow details from API
      if (template.id && actualWabaId) {
        console.log('WhatsAppFlowBuilder: Fetching full flow details for ID:', template.id, 'with wabaId:', actualWabaId);
        
        try {
          const fullFlowResponse = await fetchFlowById(template.id, actualWabaId);
          console.log('WhatsAppFlowBuilder: Full flow response:', fullFlowResponse);
          
          if (fullFlowResponse) {
            // Convert the full flow response to FlowTemplate
            const fullFlow = convertFlowResponseToTemplate(fullFlowResponse);
            console.log('WhatsAppFlowBuilder: Converted full flow:', fullFlow);
            
            // Extract preview URL from response
            // API response: { success: true, data: { preview: { preview_url: "..." } } }
            // fetchFlowById returns response.data.data, so fullFlowResponse is the data object
            let previewUrl: string | undefined;
            if ((fullFlowResponse as any)?.preview?.preview_url) {
              previewUrl = (fullFlowResponse as any).preview.preview_url;
            } else if ((fullFlowResponse as any)?.data?.preview?.preview_url) {
              previewUrl = (fullFlowResponse as any).data.preview.preview_url;
            }
            
            const newFlow = {
              ...fullFlow,
              id: template.id, // Keep original ID
              name: flowName || fullFlow.name || template.name,
              description: flowDescription || fullFlow.description || template.description,
              previewUrl: previewUrl // Add preview URL
            };
            
            console.log('WhatsAppFlowBuilder: Preview URL:', previewUrl);
            console.log('WhatsAppFlowBuilder: Full response structure:', fullFlowResponse);
            
            // Set the flow and open preview URL in new tab to avoid cookie consent dialog in iframe
            setCurrentFlow(newFlow);
            setSelectedFlow(newFlow);
            setPreviewScreenIndex(0);
            
            // Open preview URL directly in new tab to avoid cookie consent issues in iframe
            if (previewUrl) {
              window.open(previewUrl, '_blank', 'noopener,noreferrer');
              setLoading(false);
            } else {
              // If no preview URL, show modal with message
              setShowFlowPreview(true);
              setLoading(false);
            }
            return;
          }
        } catch (error: any) {
          console.error('WhatsAppFlowBuilder: Error fetching full flow details:', error);
          
          // Handle authentication errors - don't redirect, just show error
          // makeAuthenticatedRequest will handle token refresh automatically
          if (error.message && (error.message.includes('Authentication') || error.message.includes('token') || error.message.includes('refresh'))) {
            console.error('WhatsAppFlowBuilder: Authentication error - token refresh was attempted');
            // Token refresh should have been attempted - just show error, don't redirect
            alert('Failed to load flow details. Please refresh the page and try again.');
            setLoading(false);
            return;
          }
          
          // For other errors, show alert
          alert('Failed to load flow details: ' + (error.message || 'Unknown error'));
          setLoading(false);
          return;
        }
      }
      
      // Fallback if no flow ID or wabaId
      console.error('WhatsAppFlowBuilder: Flow ID or WABA ID is missing. Flow ID:', template.id, 'WABA ID:', actualWabaId);
      alert('Flow ID or WABA ID is missing. Cannot load flow details.');
      setLoading(false);
    } catch (error: any) {
      console.error('WhatsAppFlowBuilder: Error in handleStartFromTemplate:', error);
      setLoading(false);
      alert('Failed to load flow: ' + (error.message || 'Unknown error'));
    }
  };

  // Delete flow
  const handlePreviewFlow = async (flowId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    
    try {
      setLoading(true);
      
      // Get wabaId from sessionStorage if not in state
      const actualWabaId = wabaId || sessionStorage.getItem('wabaId');
      
      if (!actualWabaId) {
        alert('WABA ID is missing. Cannot preview flow.');
        setLoading(false);
        return;
      }
      
      console.log('WhatsAppFlowBuilder: Previewing flow with flowId:', flowId, 'wabaId:', actualWabaId);
      
      // Try to fetch flow meta first
      let flowMeta = null;
      try {
        flowMeta = await fetchFlowMeta(flowId, actualWabaId);
        console.log('WhatsAppFlowBuilder: Flow meta from fetchFlowMeta:', flowMeta);
      } catch (metaError: any) {
        console.warn('WhatsAppFlowBuilder: Error fetching flow meta, trying fetchFlowById:', metaError);
        // Fallback to fetchFlowById
        try {
          flowMeta = await fetchFlowById(flowId, actualWabaId);
          console.log('WhatsAppFlowBuilder: Flow meta from fetchFlowById:', flowMeta);
        } catch (fetchError: any) {
          console.error('WhatsAppFlowBuilder: Error fetching flow by ID:', fetchError);
        }
      }
      
      // Check if flow has a preview URL in meta
      // API response structure: { success: true, data: { preview: { preview_url: "..." } } }
      let previewUrl = null;
      if (flowMeta) {
        // Extract preview URL from the correct path: data.preview.preview_url
        previewUrl = flowMeta.preview?.preview_url || 
                     flowMeta.previewUrl || 
                     flowMeta.preview_url || 
                     flowMeta.facebookPreviewUrl || 
                     flowMeta.meta?.previewUrl ||
                     flowMeta.previewLink ||
                     flowMeta.facebook_preview_url;
        
        console.log('WhatsAppFlowBuilder: Preview URL from meta:', previewUrl);
        console.log('WhatsAppFlowBuilder: Full flowMeta:', flowMeta);
      }
      
      if (previewUrl) {
        // Open Facebook preview page from meta
        console.log('WhatsAppFlowBuilder: Opening preview URL:', previewUrl);
        window.open(previewUrl, '_blank');
      } else {
        // Fallback: Show error message
        console.error('WhatsAppFlowBuilder: No preview URL or Facebook Flow ID found in meta');
        alert('Preview URL not available. The flow may not be published to Facebook yet, or the preview link needs to be generated.');
      }
      
    } catch (error: any) {
      console.error('WhatsAppFlowBuilder: Error previewing flow:', error);
      alert(`Error opening preview: ${error.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFlow = async (flowId: string, flowName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${flowName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Get wabaId from sessionStorage if not in state
      const actualWabaId = wabaId || sessionStorage.getItem('wabaId');
      
      if (!actualWabaId) {
        alert('WABA ID is missing. Cannot delete flow.');
        setLoading(false);
        return;
      }
      
      await deleteFlow(flowId, actualWabaId);
      
      // Refresh flows list
      if (actualWabaId) {
        try {
          const flowResponses = await fetchFlows(actualWabaId);
          const convertedFlows = flowResponses.map(convertFlowResponseToTemplate);
          setFlows(convertedFlows);
        } catch (error: any) {
          console.error('WhatsAppFlowBuilder: Error refreshing flows after delete:', error);
        }
      }
      
      // Show success message
      alert(`Flow "${flowName}" deleted successfully.`);
    } catch (error: any) {
      console.error('WhatsAppFlowBuilder: Error deleting flow:', error);
      alert('Failed to delete flow: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Create blank flow
  const handleCreateBlank = () => {
    const newFlow: FlowTemplate = {
      id: `flow_${Date.now()}`,
      name: flowName || 'New Flow',
      description: flowDescription || 'Custom WhatsApp Flow',
      category: 'Custom',
      icon: '📝',
      screens: [{
        id: `screen_${Date.now()}`,
        name: 'Screen 1',
        screenId: 'SCREEN_1',
        fields: []
      }]
    };
    setCurrentFlow(newFlow);
    setShowTemplateSelection(false);
    setShowFlowEditor(true);
    setSelectedScreen(newFlow.screens[0]);
  };

  // Parse JSON and convert to FlowTemplate
  const parseJsonToFlow = (jsonString: string): FlowTemplate | null => {
    try {
      const jsonData = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
      
      if (!jsonData || !jsonData.screens || !Array.isArray(jsonData.screens)) {
        throw new Error('Invalid JSON format: screens array is required');
      }

      const screens: FlowScreen[] = jsonData.screens.map((screen: any, index: number) => {
        const fields: FlowField[] = [];
        
        // Extract fields from layout children (Form children and direct text components)
        if (screen.layout && screen.layout.children) {
          screen.layout.children.forEach((layoutChild: any) => {
            // Handle direct text components in layout (TextHeading, TextSubheading, TextBody)
            if (layoutChild.type === 'TextHeading') {
              fields.push({
                id: `field_${Date.now()}_${fields.length}`,
                key: `heading_${fields.length}`,
                label: 'Heading',
                type: 'heading',
                text: layoutChild.text || 'Heading'
              });
            } else if (layoutChild.type === 'TextSubheading') {
              fields.push({
                id: `field_${Date.now()}_${fields.length}`,
                key: `subheading_${fields.length}`,
                label: 'Subheading',
                type: 'subheading',
                text: layoutChild.text || 'Subheading'
              });
            } else if (layoutChild.type === 'TextBody') {
              fields.push({
                id: `field_${Date.now()}_${fields.length}`,
                key: `body_${fields.length}`,
                label: 'Body text',
                type: 'body',
                text: layoutChild.text || 'Body text'
              });
            }
            
            // Handle Form children
            if (layoutChild.type === 'Form' && layoutChild.children) {
              layoutChild.children.forEach((formChild: any) => {
                // Skip Footer, Image - only process form fields
                if (formChild.type === 'TextInput' || formChild.type === 'TextArea' || formChild.type === 'RadioButtonsGroup' || 
                    formChild.type === 'Dropdown' || formChild.type === 'Checkbox' || formChild.type === 'CheckboxGroup' ||
                    formChild.type === 'TextHeading' || formChild.type === 'TextSubheading' ||
                    formChild.type === 'TextBody' || formChild.type === 'OptIn' || formChild.type === 'List' ||
                    formChild.type === 'DatePicker') {
                  
                  let fieldType: 'text' | 'dropdown' | 'date' | 'time' | 'number' | 'boolean' | 'list' | 'checkboxgroup' | 'heading' | 'subheading' | 'body' | 'optin' | 'textarea' | 'datepicker' = 'text';
                  let options: { id: string; title: string }[] | undefined;
                  let listItems: string[] | undefined;
                  let listDataSource: string | undefined;
                  let itemTemplate: ListItemTemplate | undefined;
                  let text: string | undefined;
                  let navigateTo: string | undefined;
                  let dataSource: any = undefined; // Store data-source expression or array

                  if (formChild.type === 'DatePicker') {
                    fieldType = 'datepicker';
                  } else if (formChild.type === 'RadioButtonsGroup' || formChild.type === 'Dropdown') {
                    fieldType = 'dropdown';
                    // Handle both array and expression string for data-source
                    dataSource = formChild['data-source'];
                    if (Array.isArray(dataSource)) {
                      options = dataSource;
                    } else if (typeof dataSource === 'string' && dataSource.startsWith('${')) {
                      // It's an expression, extract example data from screen.data if available
                      const fieldName = dataSource.replace(/\$\{data\.(.*)\}/, '$1');
                      if (screen.data && screen.data[fieldName] && screen.data[fieldName].__example__) {
                        options = screen.data[fieldName].__example__;
                      }
                    }
                  } else if (formChild.type === 'CheckboxGroup') {
                    fieldType = 'checkboxgroup';
                    dataSource = formChild['data-source'];
                    if (Array.isArray(dataSource)) {
                      options = dataSource;
                    } else if (typeof dataSource === 'string' && dataSource.startsWith('${')) {
                      const fieldName = dataSource.replace(/\$\{data\.(.*)\}/, '$1');
                      if (screen.data && screen.data[fieldName] && screen.data[fieldName].__example__) {
                        options = screen.data[fieldName].__example__;
                      }
                    }
                  } else if (formChild.type === 'Checkbox') {
                    fieldType = 'boolean';
                  } else if (formChild.type === 'TextHeading') {
                    fieldType = 'heading';
                    text = formChild.text || formChild.label || 'Heading';
                  } else if (formChild.type === 'TextSubheading') {
                    fieldType = 'subheading';
                    text = formChild.text || formChild.label || 'Subheading';
                  } else if (formChild.type === 'TextBody') {
                    fieldType = 'body';
                    text = formChild.text || formChild.label || 'Body text';
                  } else if (formChild.type === 'OptIn') {
                    fieldType = 'optin';
                    // Extract navigation target if exists
                    if (formChild['on-click-action']?.next?.name) {
                      navigateTo = formChild['on-click-action'].next.name;
                    }
                  } else if (formChild.type === 'List') {
                    fieldType = 'list';
                    // Handle simple list with items array
                    if (formChild.items && Array.isArray(formChild.items)) {
                      listItems = formChild.items;
                    }
                    // Handle advanced list with data-source and item-template
                    if (formChild['data-source']) {
                      listDataSource = formChild['data-source'];
                    }
                    if (formChild['item-template']) {
                      itemTemplate = formChild['item-template'];
                    }
                  } else if (formChild.type === 'TextArea') {
                    fieldType = 'textarea';
                  } else if (formChild.type === 'TextInput') {
                    const inputType = formChild['input-type'];
                    if (inputType === 'number') {
                      fieldType = 'number';
                    } else {
                      // Default to text for date, time, and other types
                      fieldType = 'text';
                    }
                  }

                  fields.push({
                    id: `field_${Date.now()}_${fields.length}`,
                    key: formChild.name || (fieldType === 'heading' || fieldType === 'subheading' || fieldType === 'body' ? `${fieldType}_${fields.length}` : `field_${fields.length}`),
                    label: formChild.label || (fieldType === 'heading' ? 'Heading' : fieldType === 'subheading' ? 'Subheading' : fieldType === 'body' ? 'Body text' : 'Untitled Field'),
                    type: fieldType,
                    options: options,
                    listItems: listItems,
                    listDataSource: listDataSource,
                    itemTemplate: itemTemplate,
                    text: text,
                    navigateTo: navigateTo,
                    required: formChild.required || false,
                    enabled: formChild.enabled !== false,
                    value: formChild.value
                  });
                }
              });
            }
            
            // Extract cover image
            if (layoutChild.type === 'Image' && layoutChild.src) {
              // Cover image will be handled separately
            }
          });
        }

        // Extract cover image from layout children
        let coverImage: string | undefined;
        if (screen.layout && screen.layout.children) {
          const imageChild = screen.layout.children.find((child: any) => child.type === 'Image');
          if (imageChild && imageChild.src) {
            // Remove leading slash if present
            coverImage = imageChild.src.startsWith('/') ? imageChild.src.slice(1) : imageChild.src;
          }
        }

        return {
          id: `screen_${Date.now()}_${index}`,
          name: screen.title || `Screen ${index + 1}`,
          screenId: screen.id || `SCREEN_${index + 1}`,
          fields: fields,
          coverImage: coverImage,
          coverImageHeight: screen.layout?.children?.find((c: any) => c.type === 'Image')?.height
        };
      });

      const flowTemplate: FlowTemplate = {
        id: `flow_${Date.now()}`,
        name: flowName || 'Imported Flow',
        description: flowDescription || 'Flow imported from JSON',
        category: 'Custom',
        icon: '📥',
        screens: screens
      };

      return flowTemplate;
    } catch (error: any) {
      console.error('Error parsing JSON:', error);
      alert(`Failed to parse JSON: ${error.message}`);
      return null;
    }
  };

  // Handle Flow Publish
  const handlePublishFlow = async () => {
    if (!publishFlowName.trim()) {
      alert('Please enter a flow name');
      return;
    }

    if (!publishFlowJson.trim()) {
      alert('Please provide flow JSON');
      return;
    }

    try {
      // Parse JSON to validate it
      let flowJsonObject;
      try {
        flowJsonObject = JSON.parse(publishFlowJson);
      } catch (e) {
        alert('Invalid JSON. Please check your flow JSON.');
        return;
      }

      setLoading(true);

      // Get wabaId from sessionStorage if not in state
      const actualWabaId = wabaId || sessionStorage.getItem('wabaId');

      if (!actualWabaId) {
        alert('WABA ID is missing. Cannot publish flow.');
        setLoading(false);
        return;
      }

      // Construct endpoint_uri
      const endpointUri = `https://app.frontdesk24.com/flows/${actualWabaId}/endpoint`;

      // Prepare payload
      const payload = {
        name: publishFlowName.trim(),
        categories: [publishCategory],
        flowJson: flowJsonObject,
        publish: publishFlow,
        endpoint_uri: endpointUri
      };

      console.log('WhatsAppFlowBuilder: Publishing flow with payload:', payload);

      // Call API
      const response = await createFlow(actualWabaId, payload);

      alert('Flow published successfully!');
      
      // Reset form
      setPublishFlowName('');
      setPublishFlowJson('');
      setPublishCategory('OTHER');
      setPublishFlow(true);
      setShowImportJson(false);

      // Refresh flows list
      if (actualWabaId) {
        try {
          const flowResponses = await fetchFlows(actualWabaId);
          const convertedFlows = flowResponses.map(convertFlowResponseToTemplate);
          setFlows(convertedFlows);
        } catch (error: any) {
          console.error('WhatsAppFlowBuilder: Error refreshing flows after publish:', error);
        }
      }
    } catch (error: any) {
      console.error('WhatsAppFlowBuilder: Error publishing flow:', error);
      alert('Failed to publish flow: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Add new screen
  const handleAddScreen = () => {
    if (!currentFlow) return;
    const newScreen: FlowScreen = {
      id: `screen_${Date.now()}`,
      name: `Screen ${currentFlow.screens.length + 1}`,
      screenId: `SCREEN_${currentFlow.screens.length + 1}`.toUpperCase(),
      fields: []
    };
    setCurrentFlow({
      ...currentFlow,
      screens: [...currentFlow.screens, newScreen]
    });
    setSelectedScreen(newScreen);
  };

  // Add field to screen
  const handleAddField = (screenId: string) => {
    if (!currentFlow) return;
    const screen = currentFlow.screens.find(s => s.id === screenId);
    if (!screen) return;

    const newField: FlowField = {
      id: `field_${Date.now()}`,
      key: `field_${screen.fields.length + 1}`,
      label: `Field ${screen.fields.length + 1}`,
      type: 'text',
      required: false
    };

    // Initialize listItems if type is list
    if (newField.type === 'list') {
      newField.listItems = [];
    }

    const updatedScreens = currentFlow.screens.map(s =>
      s.id === screenId
        ? { ...s, fields: [...s.fields, newField] }
        : s
    );

    setCurrentFlow({ ...currentFlow, screens: updatedScreens });
    setSelectedField(newField);
  };

  // Delete field
  const handleDeleteField = (screenId: string, fieldId: string) => {
    if (!currentFlow) return;
    const updatedScreens = currentFlow.screens.map(s =>
      s.id === screenId
        ? { ...s, fields: s.fields.filter(f => f.id !== fieldId) }
        : s
    );
    setCurrentFlow({ ...currentFlow, screens: updatedScreens });
    if (selectedField?.id === fieldId) {
      setSelectedField(null);
    }
  };

  // Delete screen
  const handleDeleteScreen = (screenId: string) => {
    if (!currentFlow || currentFlow.screens.length <= 1) return;
    const updatedScreens = currentFlow.screens.filter(s => s.id !== screenId);
    setCurrentFlow({ ...currentFlow, screens: updatedScreens });
    if (selectedScreen?.id === screenId) {
      setSelectedScreen(updatedScreens[0] || null);
    }
  };

  // Convert image file to base64
  const convertImageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Extract base64 string (remove data:image/png;base64, prefix)
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle cover image upload
  const handleCoverImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, screenId: string) => {
    const file = event.target.files?.[0];
    if (!file || !currentFlow) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    try {
      const base64String = await convertImageToBase64(file);
      const imageUrl = URL.createObjectURL(file);
      
      // Update the screen with cover image
      const updatedScreens = currentFlow.screens.map(s =>
        s.id === screenId
          ? { ...s, coverImage: base64String, coverImageHeight: 108 }
          : s
      );
      
      setCurrentFlow({ ...currentFlow, screens: updatedScreens });
      
      if (selectedScreen?.id === screenId) {
        setSelectedScreen({ ...selectedScreen, coverImage: base64String, coverImageHeight: 108 });
        setCoverImagePreview(imageUrl);
      }
    } catch (error) {
      console.error('Error converting image to base64:', error);
      alert('Failed to process image');
    }

    // Reset input
    event.target.value = '';
  };

  // Remove cover image
  const handleRemoveCoverImage = (screenId: string) => {
    if (!currentFlow) return;
    
    const updatedScreens = currentFlow.screens.map(s =>
      s.id === screenId
        ? { ...s, coverImage: undefined, coverImageHeight: undefined }
        : s
    );
    
    setCurrentFlow({ ...currentFlow, screens: updatedScreens });
    
    if (selectedScreen?.id === screenId) {
      setSelectedScreen({ ...selectedScreen, coverImage: undefined, coverImageHeight: undefined });
      setCoverImagePreview(null);
    }
  };

  // Update field
  const handleUpdateField = (screenId: string, fieldId: string, updates: Partial<FlowField>) => {
    if (!currentFlow) return;
    const updatedScreens = currentFlow.screens.map(s =>
      s.id === screenId
        ? {
            ...s,
            fields: s.fields.map(f =>
              f.id === fieldId ? { ...f, ...updates } : f
            )
          }
        : s
    );
    setCurrentFlow({ ...currentFlow, screens: updatedScreens });
    if (selectedField?.id === fieldId) {
      setSelectedField({ ...selectedField, ...updates });
    }
  };

  // Helper function to convert field type to JSON type
  const getFieldTypeForJson = (type: string): string => {
    switch (type) {
      case 'text':
        return 'TextInput';
      case 'textarea':
        return 'TextArea';
      case 'number':
        return 'TextInput';
      case 'date':
        return 'TextInput';
      case 'datepicker':
        return 'DatePicker';
      case 'time':
        return 'TextInput';
      case 'dropdown':
        return 'RadioButtonsGroup'; // Can also be 'Dropdown' for dynamic data
      case 'boolean':
        return 'Checkbox';
      case 'checkboxgroup':
        return 'CheckboxGroup';
      case 'heading':
        return 'TextHeading';
      case 'subheading':
        return 'TextSubheading';
      case 'body':
        return 'TextBody';
      case 'optin':
        return 'OptIn';
      case 'list':
        return 'List';
      default:
        return 'TextInput';
    }
  };

  // Generate flow code
  const generateFlowCode = () => {
    if (!currentFlow) return '';

    // Generate JSON structure for flow
    const flowJson: any = {
      version: "7.2",
      screens: currentFlow.screens.map((screen, index) => {
        const isLastScreen = index === currentFlow.screens.length - 1;
        const isTerminal = isLastScreen; // Last screen is always terminal
        
        const screenJson: any = {
          id: screen.screenId,
          title: screen.name,
          data: {},
          terminal: isTerminal,
          layout: {
            type: "SingleColumnLayout",
            children: []
          }
        };
        
        // Only add success property for terminal screens
        if (isTerminal) {
          screenJson.success = true;
        }

        // Add cover image if exists
        if (screen.coverImage) {
          screenJson.layout.children.push({
            type: "Image",
            src: `/${screen.coverImage}`,
            height: screen.coverImageHeight || 108,
            "scale-type": "cover"
          });
        }

        // Add form with fields
        const formChildren: any[] = screen.fields
          .filter(field => {
          // Text-only fields don't need keys (heading, subheading, body)
          if (field.type === 'heading' || field.type === 'subheading' || field.type === 'body') return true;
          
          // OptIn fields need labels but keys can be auto-generated
          if (field.type === 'optin') return true;
          
          // Filter out fields without keys
          if (!field.key || !field.key.trim().length) return false;
          
          // Filter out RadioButtonsGroup (dropdown) and CheckboxGroup fields without valid options
          // (validation requires at least 1 item in data-source)
          if (field.type === 'dropdown' || field.type === 'checkboxgroup') {
            if (!field.options || field.options.length === 0) return false;
            // Check if there's at least one valid option (with id or title)
            const hasValidOption = field.options.some(opt => opt && (opt.id || opt.title));
            if (!hasValidOption) return false;
          }
          
          return true;
          })
          .map((field) => {
            const fieldType = getFieldTypeForJson(field.type);
            
            // Handle text-only fields (special cases - no name required)
            if (fieldType === 'TextHeading') {
              return {
                type: 'TextHeading',
                text: field.text || field.label || 'Heading'
              };
            }
            if (fieldType === 'TextSubheading') {
              return {
                type: 'TextSubheading',
                text: field.text || field.label || 'Subheading'
              };
            }
            if (fieldType === 'TextBody') {
              return {
                type: 'TextBody',
                text: field.text || field.label || 'Body text'
              };
            }
            
            // Handle OptIn fields
            if (fieldType === 'OptIn') {
              const optInJson: any = {
                type: 'OptIn',
                label: (field.label && field.label.trim()) || 'I agree',
                name: field.key.trim() || `optin_${Date.now()}`,
                required: field.required || false
              };
              
              // Add navigation if specified
              if (field.navigateTo) {
                optInJson['on-click-action'] = {
                  name: 'navigate',
                  payload: {},
                  next: {
                    type: 'screen',
                    name: field.navigateTo
                  }
                };
              }
              
              return optInJson;
            }
            
            const fieldJson: any = {
              type: fieldType,
              name: field.key.trim(),
              label: (field.label && field.label.trim()) || field.key.trim() || 'Untitled Field',
              required: field.required || false
            };

            // Handle RadioButtonsGroup (dropdown) - MUST have data-source with at least 1 item
            if (fieldType === 'RadioButtonsGroup') {
              // Filter and map valid options (we already filtered out fields without valid options)
              const validOptions = field.options!
                .filter(opt => opt && (opt.id || opt.title)) // Keep if has id or title
                .map(opt => ({
                  id: (opt.id && opt.id.trim()) || `option_${Date.now()}_${Math.random()}`,
                  title: (opt.title && opt.title.trim()) || 'Untitled Option'
                }));
              
              // data-source MUST have at least 1 item
              fieldJson["data-source"] = validOptions.length > 0 ? validOptions : [{
                id: 'option_1',
                title: 'Option 1'
              }];
            }
            // Handle CheckboxGroup - MUST have data-source with at least 1 item (similar to RadioButtonsGroup)
            else if (fieldType === 'CheckboxGroup') {
              // Filter and map valid options (we already filtered out fields without valid options)
              const validOptions = field.options!
                .filter(opt => opt && (opt.id || opt.title)) // Keep if has id or title
                .map(opt => ({
                  id: (opt.id && opt.id.trim()) || `option_${Date.now()}_${Math.random()}`,
                  title: (opt.title && opt.title.trim()) || 'Untitled Option'
                }));
              
              // data-source MUST have at least 1 item
              fieldJson["data-source"] = validOptions.length > 0 ? validOptions : [{
                id: 'option_1',
                title: 'Option 1'
              }];
            }
            // Handle TextInput fields - MUST have valid input-type
            else if (fieldType === 'TextInput') {
              // Valid input-type values: text, number, password, passcode, phone, email
              if (field.type === 'number') {
                fieldJson["input-type"] = "number";
              } else if (field.type === 'date' || field.type === 'time') {
                // Date/time not in valid list, use text with appropriate placeholder hint
                fieldJson["input-type"] = "text";
              } else {
                fieldJson["input-type"] = "text";
              }
            }
            // Handle TextArea fields - no input-type needed
            else if (fieldType === 'TextArea') {
              // TextArea doesn't need input-type
            }
            // Handle DatePicker fields - no input-type needed
            else if (fieldType === 'DatePicker') {
              // DatePicker doesn't need input-type, it's a native date picker
            }
            // Handle List fields
            else if (fieldType === 'List') {
              // Simple list with items array
              if (field.listItems && field.listItems.length > 0) {
                const validItems = field.listItems.filter(item => item && typeof item === 'string' && item.trim().length > 0);
                if (validItems.length > 0) {
                  fieldJson.items = validItems;
                }
              }
              // Advanced list with data-source and item-template
              if (field.listDataSource) {
                fieldJson["data-source"] = field.listDataSource;
              }
              if (field.itemTemplate) {
                fieldJson["item-template"] = field.itemTemplate;
              }
            }
            // Handle Checkbox (boolean) - doesn't need input-type

            return fieldJson;
          })
          .filter(field => field !== null && field !== undefined); // Remove any null/undefined fields

        // Add footer button
        if (screen.fields.length > 0) {
          const nextScreen = !isTerminal && currentFlow.screens[index + 1];
          
          formChildren.push({
            type: "Footer",
            label: isTerminal ? "Done" : "Continue",
            "on-click-action": isTerminal 
              ? {
                  name: "complete",
                  payload: {}
                }
              : nextScreen
              ? {
                  name: "navigate",
                  next: {
                    type: "screen",
                    name: nextScreen.screenId
                  }
                }
              : {
                  name: "complete",
                  payload: {}
                }
          });
        }

        screenJson.layout.children.push({
          type: "Form",
          name: "form",
          children: formChildren
        });

        return screenJson;
      })
    };

    const screensObject = currentFlow.screens.reduce((acc, screen) => {
      const screenData: any = {};
      
      screen.fields.forEach(field => {
        if (field.type === 'dropdown' && field.options) {
          screenData[field.key] = field.options;
        } else if (field.type === 'list' && field.listItems) {
          screenData[field.key] = field.listItems;
        } else if (field.type === 'list') {
          screenData[field.key] = [];
        } else {
          screenData[field.key] = field.value || '';
        }
      });

      acc[screen.screenId] = {
        screen: screen.screenId,
        data: screenData
      };
      
      return acc;
    }, {} as any);

    // Return JSON structure
    return JSON.stringify(flowJson, null, 2);
  };

  // Convert API FlowResponse to FlowTemplate
  const convertFlowResponseToTemplate = (flowResponse: FlowResponse | any): FlowTemplate => {
    // Handle different API response formats
    // API returns: { id, name, categories: ["OTHER"], status: "PUBLISHED" }
    
    console.log('WhatsAppFlowBuilder: Converting flow response:', flowResponse);
    
    // Ensure ID is a string - API returns flowId, not id
    const flowId = (flowResponse.flowId || flowResponse.id) ? String(flowResponse.flowId || flowResponse.id) : String(Date.now());
    
    // Extract category from categories array (use first one or default)
    const categories = (flowResponse as any).categories || [];
    const category = categories.length > 0 ? categories[0] : 'Custom';
    console.log('WhatsAppFlowBuilder: Extracted category:', category, 'from categories:', categories);
    
    // Generate icon based on category
    const iconMap: Record<string, string> = {
      'OTHER': '📋',
      'FINANCE': '💰',
      'SERVICE': '📅',
      'INSURANCE': '🛡️',
      'ECOMMERCE': '🛒'
    };
    const icon = iconMap[category] || flowResponse.icon || '📋';
    
    // If screens are already in FlowScreen format, use them directly
    // Otherwise, create default empty screen
    let screens: FlowScreen[] = [];
    
    if (flowResponse.screens && Array.isArray(flowResponse.screens)) {
      screens = flowResponse.screens.map((screen: any, index: number) => {
        // If screen is already in FlowScreen format
        if (screen.fields && Array.isArray(screen.fields)) {
          return {
            id: screen.id || `screen_${index}`,
            name: screen.name || screen.title || `Screen ${index + 1}`,
            screenId: screen.screenId || screen.id || `SCREEN_${index + 1}`,
            fields: screen.fields || [],
            navigation: screen.navigation || [],
            coverImage: screen.coverImage,
            coverImageHeight: screen.coverImageHeight
          };
        }
        // Otherwise, create empty screen
        return {
          id: screen.id || `screen_${index}`,
          name: screen.name || screen.title || `Screen ${index + 1}`,
          screenId: screen.screenId || screen.id || `SCREEN_${index + 1}`,
          fields: [] as FlowField[],
          navigation: [] as NavigationRule[]
        };
      });
    }
    
    // If no screens provided, create default empty screen
    if (screens.length === 0) {
      screens = [{
        id: 'screen_1',
        name: 'Screen 1',
        screenId: 'SCREEN_1',
        fields: []
      }];
    }
    
    // Extract name and description from API response structure
    // API structure: { flowId, flowName, bot_settings: { description }, ... }
    const flowName = flowResponse.flowName || flowResponse.name || 'Untitled Flow';
    const flowDescription = flowResponse.bot_settings?.description || 
                            flowResponse.description || 
                            flowResponse.flowName || 
                            'No description';
    
    const converted: FlowTemplate = {
      id: flowId,
      name: flowName,
      description: flowDescription,
      category: flowResponse.category || category,
      icon: icon,
      screens: screens
    };
    
    console.log('WhatsAppFlowBuilder: Converted flow:', converted);
    return converted;
  };

  // Fetch flows from API on mount
  useEffect(() => {
    console.log('WhatsAppFlowBuilder: Fetch useEffect triggered');
    
    const loadFlows = async () => {
      // Get wabaId directly from sessionStorage
      const actualWabaId = sessionStorage.getItem('wabaId');
      
      console.log('WhatsAppFlowBuilder: wabaId from sessionStorage:', actualWabaId);
      
      if (!actualWabaId) {
        console.warn('WhatsAppFlowBuilder: wabaId is missing, cannot fetch flows');
        setFlowsLoading(false);
        setFlowsError('WABA ID is missing. Please select a WhatsApp account.');
        return;
      }

      // Update state
      setWabaId(actualWabaId);
      setFlowsLoading(true);
      setFlowsError(null);
      
      try {
        console.log('WhatsAppFlowBuilder: Starting to fetch flows for wabaId:', actualWabaId);
        const flowResponses = await fetchFlows(actualWabaId);
        console.log('WhatsAppFlowBuilder: Raw flow responses:', flowResponses);
        console.log('WhatsAppFlowBuilder: Raw flow responses count:', flowResponses.length);
        console.log('WhatsAppFlowBuilder: Raw flow responses type:', typeof flowResponses, Array.isArray(flowResponses));
        
        if (!Array.isArray(flowResponses)) {
          console.error('WhatsAppFlowBuilder: flowResponses is not an array:', flowResponses);
          setFlows([]);
          return;
        }
        
        const convertedFlows = flowResponses.map(convertFlowResponseToTemplate);
        console.log('WhatsAppFlowBuilder: Converted flows:', convertedFlows);
        console.log('WhatsAppFlowBuilder: Converted flows count:', convertedFlows.length);
        setFlows(convertedFlows);
        console.log('WhatsAppFlowBuilder: Set flows in state. Flows count:', convertedFlows.length);
      } catch (error: any) {
        console.error('WhatsAppFlowBuilder: Error loading flows:', error);
        
        // Handle authentication errors - don't redirect, just show error
        // makeAuthenticatedRequest will handle token refresh automatically
        if (error.message && (error.message.includes('Authentication') || error.message.includes('token'))) {
          console.error('WhatsAppFlowBuilder: Authentication error detected');
          // Token refresh should have been attempted by makeAuthenticatedRequest
          // Just show error - user can refresh page or retry
          setFlowsError('Authentication failed. Please refresh the page or try again.');
        } else if (error.response && (error.response.status === 402 || error.response.status === 401 || error.response.status === 403)) {
          console.error('WhatsAppFlowBuilder: Authentication error - makeAuthenticatedRequest should have handled token refresh');
          // Token refresh should have been attempted - show error
          setFlowsError('Authentication failed. Please refresh the page or try again.');
        } else {
          setFlowsError(error.message || 'Failed to load flows. Please try again later.');
        }
        
        setFlows([]);
      } finally {
        setFlowsLoading(false);
      }
    };

    loadFlows();
  }, []); // Run once on mount

  // Debug: Log flows state changes
  useEffect(() => {
    console.log('WhatsAppFlowBuilder: flows state changed:', flows);
    console.log('WhatsAppFlowBuilder: flows length:', flows.length);
    console.log('WhatsAppFlowBuilder: flowsLoading:', flowsLoading);
    console.log('WhatsAppFlowBuilder: flowsError:', flowsError);
  }, [flows, flowsLoading, flowsError]);

  // Update cover image preview when selected screen changes
  useEffect(() => {
    if (selectedScreen?.coverImage) {
      // Create a preview URL from base64
      const imageUrl = `data:image/png;base64,${selectedScreen.coverImage}`;
      setCoverImagePreview(imageUrl);
    } else {
      setCoverImagePreview(null);
    }
  }, [selectedScreen]);

  if (loading) {
    return <LoadingComponent loading={true} message="Loading flow builder..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">WhatsApp Flow Builder</h1>
              <p className="text-gray-600 text-lg">Create interactive WhatsApp Flows for your business</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        {/* Template Selection */}
        {showTemplateSelection && (
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">

              {/* Loading State */}
              {flowsLoading && (
                <div className="flex items-center justify-center py-12">
                  <LoadingComponent loading={true} />
                </div>
              )}

              {/* Error State */}
              {flowsError && !flowsLoading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800 text-sm">{flowsError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {/* Dynamic Templates from API */}
                {!flowsLoading && flows.length > 0 && flows.map((template) => (
                  <div
                    key={template.id}
                    className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer relative group"
                    onClick={() => handleStartFromTemplate(template)}
                  >
                    {/* Action Buttons */}
                    <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      {/* Preview Button */}
                      <button
                        onClick={(e) => handlePreviewFlow(template.id, e)}
                        className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        title="Preview Flow"
                      >
                        <IoMdEye className="w-4 h-4" />
                      </button>
                      {/* Delete Button */}
                      <button
                        onClick={(e) => handleDeleteFlow(template.id, template.name, e)}
                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        title="Delete Flow"
                      >
                        <IoMdTrash className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="text-4xl mb-4">{template.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 pr-8">{template.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{template.description || 'No description'}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{template.category}</span>
                      <span className="text-xs text-blue-600">{template.screens.length} screens</span>
                    </div>
                  </div>
                ))}
                
                {/* Show message if no flows and not loading */}
                {!flowsLoading && flows.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500 text-sm">
                    No flows found. Create a new flow or import from JSON.
                  </div>
                )}

                {/* Publish Flow */}
                <div
                  className="border-2 border-green-300 rounded-xl p-6 hover:border-green-500 hover:shadow-lg transition-all cursor-pointer flex flex-col items-center justify-center"
                  onClick={() => {
                    // Generate flow JSON from current flow if editing, otherwise show empty form
                    if (currentFlow) {
                      const flowJson = generateFlowCode();
                      setPublishFlowJson(flowJson);
                      setPublishFlowName(currentFlow.name || '');
                    }
                    setShowImportJson(true);
                  }}
                >
                  <IoMdCode className="w-12 h-12 text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Publish Flow</h3>
                  <p className="text-gray-600 text-sm text-center">Publish your WhatsApp Flow to API</p>
                </div>

                {/* WhatsApp Flows Playground */}
                <div
                  className="border-2 border-blue-300 rounded-xl p-6 hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer flex flex-col items-center justify-center"
                  onClick={() => window.open('https://developers.facebook.com/docs/whatsapp/flows/playground/', '_blank')}
                >
                  <div className="text-4xl mb-4">🎮</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">WhatsApp Flows Playground</h3>
                  <p className="text-gray-600 text-sm text-center mb-4">Build and test flows using Facebook's official playground</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open('https://developers.facebook.com/docs/whatsapp/flows/playground/', '_blank');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Open Playground
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Flow Editor */}
        {showFlowEditor && currentFlow && (
          <div className="grid grid-cols-12 gap-6">
            {/* Left Sidebar - Screens List */}
            <div className="col-span-3">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Screens</h3>
                  <button
                    onClick={handleAddScreen}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Add Screen"
                  >
                    <IoMdAdd className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {currentFlow.screens.map((screen, index) => (
                    <div
                      key={screen.id}
                      onClick={() => setSelectedScreen(screen)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedScreen?.id === screen.id
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {index + 1}. {screen.name}
                        </span>
                        {currentFlow.screens.length > 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreen(screen.id);
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <IoMdTrash className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{screen.screenId}</span>
                      <div className="text-xs text-gray-400 mt-1">{screen.fields.length} fields</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Center - Fields Editor */}
            <div className="col-span-6">
              {selectedScreen && (
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedScreen.name}</h3>
                      <span className="text-sm text-gray-500">{selectedScreen.screenId}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={selectedScreen.name}
                        onChange={(e) => {
                          const updatedScreens = currentFlow.screens.map(s =>
                            s.id === selectedScreen.id ? { ...s, name: e.target.value } : s
                          );
                          setCurrentFlow({ ...currentFlow, screens: updatedScreens });
                          setSelectedScreen({ ...selectedScreen, name: e.target.value });
                        }}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={selectedScreen.screenId}
                        onChange={(e) => {
                          const updatedScreens = currentFlow.screens.map(s =>
                            s.id === selectedScreen.id ? { ...s, screenId: e.target.value.toUpperCase() } : s
                          );
                          setCurrentFlow({ ...currentFlow, screens: updatedScreens });
                          setSelectedScreen({ ...selectedScreen, screenId: e.target.value.toUpperCase() });
                        }}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  {/* Cover Image Section */}
                  <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image</label>
                    {coverImagePreview ? (
                      <div className="relative">
                        <img
                          src={coverImagePreview}
                          alt="Cover preview"
                          className="w-full h-auto rounded-lg mb-2 max-h-48 object-cover"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = (e) => {
                                const event = e as any;
                                handleCoverImageUpload(event, selectedScreen.id);
                              };
                              input.click();
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
                          >
                            <IoMdCreate className="w-4 h-4" />
                            Change Image
                          </button>
                          <button
                            onClick={() => handleRemoveCoverImage(selectedScreen.id)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs"
                          >
                            <IoMdTrash className="w-4 h-4" />
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <IoMdAdd className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleCoverImageUpload(e, selectedScreen.id)}
                            className="hidden"
                          />
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <button
                      onClick={() => handleAddField(selectedScreen.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <IoMdAdd className="w-4 h-4" />
                      Add Field
                    </button>
                  </div>

                  <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
                    {selectedScreen.fields.map((field) => (
                      <div
                        key={field.id}
                        className={`p-4 border-2 rounded-lg ${
                          selectedField?.id === field.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <MdDragIndicator className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">{field.label}</span>
                            <span className="text-xs text-gray-500">({field.type})</span>
                          </div>
                          <button
                            onClick={() => handleDeleteField(selectedScreen.id, field.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <IoMdTrash className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => setSelectedField(field)}
                          className="w-full text-left text-xs text-blue-600 hover:underline"
                        >
                          Click to edit field properties
                        </button>
                      </div>
                    ))}
                    {selectedScreen.fields.length === 0 && (
                      <div className="text-center py-12 text-gray-400">
                        <p>No fields added yet</p>
                        <p className="text-sm mt-2">Click "Add Field" to get started</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Field Editor */}
            <div className="col-span-3">
              {selectedField ? (
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Field Properties</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                      <input
                        type="text"
                        value={selectedField.label}
                        onChange={(e) => handleUpdateField(selectedScreen!.id, selectedField.id, { label: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Key (API field name)</label>
                      <input
                        type="text"
                        value={selectedField.key}
                        onChange={(e) => handleUpdateField(selectedScreen!.id, selectedField.id, { key: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                      <select
                        value={selectedField.type}
                        onChange={(e) => {
                          const newType = e.target.value as any;
                          const updates: Partial<FlowField> = { type: newType };
                          
                          // Initialize listItems when changing to list type
                          if (newType === 'list' && !selectedField.listItems) {
                            updates.listItems = [];
                          }
                          // Clear listItems when changing from list type
                          if (newType !== 'list' && selectedField.listItems) {
                            updates.listItems = undefined;
                          }
                          // Initialize options when changing to dropdown or checkboxgroup type
                          if ((newType === 'dropdown' || newType === 'checkboxgroup') && !selectedField.options) {
                            // Add one empty option to get started
                            updates.options = [{ id: `option_${Date.now()}`, title: '' }];
                          }
                          // Clear options when changing from dropdown or checkboxgroup type
                          if (newType !== 'dropdown' && newType !== 'checkboxgroup' && selectedField.options) {
                            updates.options = undefined;
                          }
                          // Initialize text when changing to heading/subheading/body types
                          if ((newType === 'heading' || newType === 'subheading' || newType === 'body') && !selectedField.text) {
                            updates.text = selectedField.label || (newType === 'heading' ? 'Heading' : newType === 'subheading' ? 'Subheading' : 'Body text');
                          }
                          // Clear text when changing from text-only types
                          if (newType !== 'heading' && newType !== 'subheading' && newType !== 'body' && selectedField.text) {
                            updates.text = undefined;
                          }
                          // Clear navigateTo when changing from optin type
                          if (newType !== 'optin' && selectedField.navigateTo) {
                            updates.navigateTo = undefined;
                          }
                          
                          handleUpdateField(selectedScreen!.id, selectedField.id, updates);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="text">Text</option>
                        <option value="textarea">Text Area</option>
                        <option value="dropdown">Dropdown (Radio)</option>
                        <option value="checkboxgroup">Checkbox Group</option>
                        <option value="date">Date (Text Input)</option>
                        <option value="datepicker">Date Picker</option>
                        <option value="time">Time</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean (Single Checkbox)</option>
                        <option value="optin">Opt-In</option>
                        <option value="heading">Heading</option>
                        <option value="subheading">Subheading</option>
                        <option value="body">Body Text</option>
                        <option value="list">List</option>
                      </select>
                    </div>
                    {(selectedField.type === 'heading' || selectedField.type === 'subheading' || selectedField.type === 'body') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {selectedField.type === 'heading' ? 'Heading Text' : selectedField.type === 'subheading' ? 'Subheading Text' : 'Body Text'}
                        </label>
                        <textarea
                          value={selectedField.text || ''}
                          onChange={(e) => handleUpdateField(selectedScreen!.id, selectedField.id, { text: e.target.value })}
                          placeholder={`Enter ${selectedField.type === 'heading' ? 'heading' : selectedField.type === 'subheading' ? 'subheading' : 'body'} text`}
                          rows={selectedField.type === 'body' ? 4 : 2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    )}
                    {selectedField.type === 'optin' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Navigate To (Optional)</label>
                        <select
                          value={selectedField.navigateTo || ''}
                          onChange={(e) => handleUpdateField(selectedScreen!.id, selectedField.id, { navigateTo: e.target.value || undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">No navigation</option>
                          {currentFlow?.screens.filter(s => s.id !== selectedScreen?.id).map(screen => (
                            <option key={screen.id} value={screen.screenId}>{screen.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Select a screen to navigate to when this opt-in is clicked (e.g., Terms and Conditions)</p>
                      </div>
                    )}
                    {selectedField.type === 'dropdown' && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">Options</label>
                          {(!selectedField.options || selectedField.options.length === 0) && (
                            <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        {(!selectedField.options || selectedField.options.length === 0 || 
                          (selectedField.options.length > 0 && selectedField.options.every(opt => !opt.id && !opt.title))) && (
                          <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            ⚠️ <strong>Required:</strong> Dropdown fields must have at least one valid option. Add options below (both ID and Title are required).
                          </div>
                        )}
                        <div className="space-y-2">
                          {selectedField.options?.map((opt, idx) => (
                            <div key={idx} className="flex gap-2">
                              <input
                                type="text"
                                value={opt.title}
                                onChange={(e) => {
                                  const newOptions = [...(selectedField.options || [])];
                                  newOptions[idx] = { ...opt, title: e.target.value };
                                  handleUpdateField(selectedScreen!.id, selectedField.id, { options: newOptions });
                                }}
                                placeholder="Option title"
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <input
                                type="text"
                                value={opt.id}
                                onChange={(e) => {
                                  const newOptions = [...(selectedField.options || [])];
                                  newOptions[idx] = { ...opt, id: e.target.value };
                                  handleUpdateField(selectedScreen!.id, selectedField.id, { options: newOptions });
                                }}
                                placeholder="ID"
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newOptions = [...(selectedField.options || []), { id: `option_${Date.now()}`, title: '' }];
                              handleUpdateField(selectedScreen!.id, selectedField.id, { options: newOptions });
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            + Add Option
                          </button>
                        </div>
                      </div>
                    )}
                    {selectedField.type === 'checkboxgroup' && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">Options (Multi-select)</label>
                          {(!selectedField.options || selectedField.options.length === 0) && (
                            <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        {(!selectedField.options || selectedField.options.length === 0 || 
                          (selectedField.options.length > 0 && selectedField.options.every(opt => !opt.id && !opt.title))) && (
                          <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            ⚠️ <strong>Required:</strong> Checkbox Group fields must have at least one valid option. Add options below (both ID and Title are required).
                          </div>
                        )}
                        <div className="space-y-2">
                          {selectedField.options?.map((opt, idx) => (
                            <div key={idx} className="flex gap-2">
                              <input
                                type="text"
                                value={opt.title}
                                onChange={(e) => {
                                  const newOptions = [...(selectedField.options || [])];
                                  newOptions[idx] = { ...opt, title: e.target.value };
                                  handleUpdateField(selectedScreen!.id, selectedField.id, { options: newOptions });
                                }}
                                placeholder="Option title"
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <input
                                type="text"
                                value={opt.id}
                                onChange={(e) => {
                                  const newOptions = [...(selectedField.options || [])];
                                  newOptions[idx] = { ...opt, id: e.target.value };
                                  handleUpdateField(selectedScreen!.id, selectedField.id, { options: newOptions });
                                }}
                                placeholder="ID"
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newOptions = [...(selectedField.options || []), { id: `option_${Date.now()}`, title: '' }];
                              handleUpdateField(selectedScreen!.id, selectedField.id, { options: newOptions });
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            + Add Option
                          </button>
                        </div>
                      </div>
                    )}
                    {selectedField.type === 'list' && (
                      <div className="space-y-4">
                        {/* List Type Toggle */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">List Type</label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleUpdateField(selectedScreen!.id, selectedField.id, { 
                                listDataSource: undefined, 
                                itemTemplate: undefined 
                              })}
                              className={`px-3 py-1.5 text-sm rounded ${
                                !selectedField.listDataSource && !selectedField.itemTemplate
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              Simple List
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateField(selectedScreen!.id, selectedField.id, { 
                                listItems: undefined,
                                listDataSource: selectedField.listDataSource || '${items}',
                                itemTemplate: selectedField.itemTemplate || {
                                  type: 'Card',
                                  children: []
                                }
                              })}
                              className={`px-3 py-1.5 text-sm rounded ${
                                selectedField.listDataSource || selectedField.itemTemplate
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              Advanced List (with Template)
                            </button>
                          </div>
                        </div>

                        {/* Simple List Items */}
                        {!selectedField.listDataSource && !selectedField.itemTemplate && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">List Items</label>
                            <div className="space-y-2">
                              {(selectedField.listItems || []).map((item, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                  <input
                                    type="text"
                                    value={item}
                                    onChange={(e) => {
                                      const newListItems = [...(selectedField.listItems || [])];
                                      newListItems[idx] = e.target.value;
                                      handleUpdateField(selectedScreen!.id, selectedField.id, { listItems: newListItems });
                                    }}
                                    placeholder="List item"
                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                  <button
                                    onClick={() => {
                                      const newListItems = [...(selectedField.listItems || [])];
                                      newListItems.splice(idx, 1);
                                      handleUpdateField(selectedScreen!.id, selectedField.id, { listItems: newListItems });
                                    }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Remove item"
                                  >
                                    <IoMdTrash className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => {
                                  const newListItems = [...(selectedField.listItems || []), ''];
                                  handleUpdateField(selectedScreen!.id, selectedField.id, { listItems: newListItems });
                                }}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                + Add Item
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Advanced List Configuration */}
                        {(selectedField.listDataSource || selectedField.itemTemplate) && (
                          <div className="space-y-4">
                            {/* Data Source */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Data Source</label>
                              <input
                                type="text"
                                value={selectedField.listDataSource || ''}
                                onChange={(e) => handleUpdateField(selectedScreen!.id, selectedField.id, { 
                                  listDataSource: e.target.value || undefined 
                                })}
                                placeholder="${items}"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                              <p className="text-xs text-gray-500 mt-1">Expression like ${`{items}`} to reference data array</p>
                            </div>

                            {/* Item Template */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Item Template (Card Children)</label>
                              <div className="space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                                {(selectedField.itemTemplate?.children || []).map((child, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-medium text-gray-700">{child.type}</span>
                                      <button
                                        onClick={() => {
                                          const newChildren = [...(selectedField.itemTemplate?.children || [])];
                                          newChildren.splice(idx, 1);
                                          handleUpdateField(selectedScreen!.id, selectedField.id, {
                                            itemTemplate: {
                                              type: 'Card',
                                              children: newChildren
                                            }
                                          });
                                        }}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      >
                                        <IoMdTrash className="w-3 h-3" />
                                      </button>
                                    </div>
                                    {child.type === 'Image' && (
                                      <input
                                        type="text"
                                        value={child.src || ''}
                                        onChange={(e) => {
                                          const newChildren = [...(selectedField.itemTemplate?.children || [])];
                                          newChildren[idx] = { ...child, src: e.target.value };
                                          handleUpdateField(selectedScreen!.id, selectedField.id, {
                                            itemTemplate: {
                                              type: 'Card',
                                              children: newChildren
                                            }
                                          });
                                        }}
                                        placeholder="${item.image}"
                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      />
                                    )}
                                    {child.type === 'Text' && (
                                      <input
                                        type="text"
                                        value={child.text || ''}
                                        onChange={(e) => {
                                          const newChildren = [...(selectedField.itemTemplate?.children || [])];
                                          newChildren[idx] = { ...child, text: e.target.value };
                                          handleUpdateField(selectedScreen!.id, selectedField.id, {
                                            itemTemplate: {
                                              type: 'Card',
                                              children: newChildren
                                            }
                                          });
                                        }}
                                        placeholder="${item.title}"
                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      />
                                    )}
                                    {child.type === 'Stepper' && (
                                      <div className="space-y-2">
                                        <input
                                          type="text"
                                          value={child.name || ''}
                                          onChange={(e) => {
                                            const newChildren = [...(selectedField.itemTemplate?.children || [])];
                                            newChildren[idx] = { ...child, name: e.target.value };
                                            handleUpdateField(selectedScreen!.id, selectedField.id, {
                                              itemTemplate: {
                                                type: 'Card',
                                                children: newChildren
                                              }
                                            });
                                          }}
                                          placeholder="qty_${item.id}"
                                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                        />
                                        <div className="flex gap-2">
                                          <input
                                            type="number"
                                            value={child.min || 0}
                                            onChange={(e) => {
                                              const newChildren = [...(selectedField.itemTemplate?.children || [])];
                                              newChildren[idx] = { ...child, min: parseInt(e.target.value) || 0 };
                                              handleUpdateField(selectedScreen!.id, selectedField.id, {
                                                itemTemplate: {
                                                  type: 'Card',
                                                  children: newChildren
                                                }
                                              });
                                            }}
                                            placeholder="Min"
                                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                          />
                                          <input
                                            type="number"
                                            value={child.max || 99}
                                            onChange={(e) => {
                                              const newChildren = [...(selectedField.itemTemplate?.children || [])];
                                              newChildren[idx] = { ...child, max: parseInt(e.target.value) || 99 };
                                              handleUpdateField(selectedScreen!.id, selectedField.id, {
                                                itemTemplate: {
                                                  type: 'Card',
                                                  children: newChildren
                                                }
                                              });
                                            }}
                                            placeholder="Max"
                                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                          />
                                          <input
                                            type="text"
                                            value={child.value || ''}
                                            onChange={(e) => {
                                              const newChildren = [...(selectedField.itemTemplate?.children || [])];
                                              newChildren[idx] = { ...child, value: e.target.value };
                                              handleUpdateField(selectedScreen!.id, selectedField.id, {
                                                itemTemplate: {
                                                  type: 'Card',
                                                  children: newChildren
                                                }
                                              });
                                            }}
                                            placeholder="${item.qty}"
                                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const newChild: { type: 'Image'; src?: string } = { type: 'Image', src: '' };
                                      const newChildren = [...(selectedField.itemTemplate?.children || []), newChild];
                                      handleUpdateField(selectedScreen!.id, selectedField.id, {
                                        itemTemplate: {
                                          type: 'Card',
                                          children: newChildren
                                        }
                                      });
                                    }}
                                    className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                  >
                                    + Image
                                  </button>
                                  <button
                                    onClick={() => {
                                      const newChild: { type: 'Text'; text?: string } = { type: 'Text', text: '' };
                                      const newChildren = [...(selectedField.itemTemplate?.children || []), newChild];
                                      handleUpdateField(selectedScreen!.id, selectedField.id, {
                                        itemTemplate: {
                                          type: 'Card',
                                          children: newChildren
                                        }
                                      });
                                    }}
                                    className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                  >
                                    + Text
                                  </button>
                                  <button
                                    onClick={() => {
                                      const newChild: { type: 'Stepper'; name?: string; min?: number; max?: number; value?: string } = { type: 'Stepper', name: '', min: 0, max: 99, value: '' };
                                      const newChildren = [...(selectedField.itemTemplate?.children || []), newChild];
                                      handleUpdateField(selectedScreen!.id, selectedField.id, {
                                        itemTemplate: {
                                          type: 'Card',
                                          children: newChildren
                                        }
                                      });
                                    }}
                                    className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                  >
                                    + Stepper
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedField.required || false}
                        onChange={(e) => handleUpdateField(selectedScreen!.id, selectedField.id, { required: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <label className="text-sm text-gray-700">Required</label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Depends On (Field Key)</label>
                      <input
                        type="text"
                        value={selectedField.dependsOn || ''}
                        onChange={(e) => handleUpdateField(selectedScreen!.id, selectedField.id, { dependsOn: e.target.value || undefined })}
                        placeholder="Field must be filled first"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                  <p className="text-gray-400 text-sm text-center">Select a field to edit its properties</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Code Preview Modal */}
      {showCodePreview && currentFlow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Generated Flow Code</h3>
              <button
                onClick={() => setShowCodePreview(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <IoMdClose className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs">
                <code>{generateFlowCode()}</code>
              </pre>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  const code = generateFlowCode();
                  navigator.clipboard.writeText(code);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <IoMdCopy className="w-5 h-5" />
                Copy Code
              </button>
              <button
                onClick={() => setShowCodePreview(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Flow Preview Modal */}
      {showFlowPreview && currentFlow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Flow Preview</h3>
                <p className="text-sm text-gray-500 mt-1">{currentFlow.name}</p>
              </div>
              <div className="flex items-center gap-3">
                {currentFlow.previewUrl && (
                  <button
                    onClick={() => {
                      window.open(currentFlow.previewUrl, '_blank', 'noopener,noreferrer');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Open in New Tab
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowFlowPreview(false);
                    setPreviewScreenIndex(0);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <IoMdClose className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden p-6 bg-gray-50">
              {currentFlow.previewUrl ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[600px] bg-white rounded-lg border border-gray-200 p-8">
                  <div className="text-center max-w-md">
                    <div className="text-6xl mb-6">🔗</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Flow Preview</h3>
                    <p className="text-gray-600 mb-6">
                      The flow preview has been opened in a new tab to avoid cookie consent issues. 
                      Please check your browser tabs.
                    </p>
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => {
                          window.open(currentFlow.previewUrl, '_blank', 'noopener,noreferrer');
                        }}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Open Preview Again
                      </button>
                      <button
                        onClick={() => {
                          setShowFlowPreview(false);
                          setPreviewScreenIndex(0);
                        }}
                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[600px] bg-white rounded-lg border border-gray-200 p-8">
                  <p className="text-gray-600 mb-4 text-center">
                    Preview URL not available for this flow.
                  </p>
                  <button
                    onClick={() => {
                      setShowFlowPreview(false);
                      setPreviewScreenIndex(0);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowFlowPreview(false);
                  setPreviewScreenIndex(0);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Flow Modal */}
      {showImportJson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Publish Flow</h3>
              <button
                onClick={() => {
                  setShowImportJson(false);
                  setPublishFlowName('');
                  setPublishFlowJson('');
                  setPublishCategory('OTHER');
                  setPublishFlow(true);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <IoMdClose className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Flow Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Flow Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={publishFlowName}
                    onChange={(e) => setPublishFlowName(e.target.value)}
                    placeholder="e.g., Customer Onboarding"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={publishCategory}
                    onChange={(e) => setPublishCategory(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="OTHER">OTHER</option>
                  </select>
                </div>

                {/* Endpoint URI */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endpoint URI
                  </label>
                  <input
                    type="text"
                    value={`https://app.frontdesk24.com/flows/${wabaId || sessionStorage.getItem('wabaId') || ''}/endpoint`}
                    readOnly
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                </div>

                {/* Publish Toggle */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={publishFlow}
                      onChange={(e) => setPublishFlow(e.target.checked)}
                      className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Publish Flow
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    If checked, the flow will be published immediately. Otherwise, it will be saved as draft.
                  </p>
                </div>

                {/* Flow JSON */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Flow JSON <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={publishFlowJson}
                    onChange={(e) => setPublishFlowJson(e.target.value)}
                    placeholder='Paste your WhatsApp Flow JSON here, e.g.:
{
  "version": "7.2",
  "screens": [
    {
      "id": "SCREEN_1",
      "title": "My Screen",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "Form",
            "name": "form",
            "children": [
              {
                "type": "TextInput",
                "name": "field_name",
                "label": "Field Label",
                "input-type": "text",
                "required": true
              }
            ]
          }
        ]
      }
    }
  ]
}'
                    rows={15}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 font-mono text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImportJson(false);
                  setPublishFlowName('');
                  setPublishFlowJson('');
                  setPublishCategory('OTHER');
                  setPublishFlow(true);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handlePublishFlow}
                disabled={loading || !publishFlowName.trim() || !publishFlowJson.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Publishing...' : 'Publish Flow'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
